import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import {
  displayDateUS,
  formatDateForInput,
  toIsoDate,
} from "../../../utils/dateUtils";
import { CloseIcon } from "../../../Icons";

const EPISODE_OFFSET_DAYS = 59;

const btnCancel =
  "inline-flex items-center justify-center h-9 px-3 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50";
const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors disabled:opacity-50";

const addDaysYmd = (ymd, days) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const ymdToDisplay = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${m}/${d}/${y}`;
};

const parseCmp = (a, b) => {
  const aa = formatDateForInput(a);
  const bb = formatDateForInput(b);
  if (!aa || !bb) return 0;
  return aa.localeCompare(bb);
};

/** Normalize form date (MM/DD/YYYY or YYYY-MM-DD) to ISO for the API. */
const toApiDate = (value) => {
  const ymd = formatDateForInput(value);
  if (!ymd) return null;
  return toIsoDate(ymd) || `${ymd}T00:00:00.000Z`;
};

/**
 * Modal to add or edit an episode (default 60-day window on create).
 * Dates can be edited anytime (Admin/OA).
 */
const EpisodeDateModal = ({
  open,
  mode = "add",
  chart,
  episode = null,
  defaultStart = "",
  onClose,
  onSubmit,
  saving = false,
}) => {
  const isEdit = mode === "edit";
  const methods = useForm({
    defaultValues: {
      startDate: "",
      endDate: "",
    },
  });
  const { handleSubmit, watch, setValue, reset, getValues } = methods;
  const startDate = watch("startDate");
  const chartStartYmd = formatDateForInput(chart?.startDate) || undefined;
  const episodeStartYmd = formatDateForInput(startDate) || undefined;

  useEffect(() => {
    if (!open) return;
    if (isEdit && episode) {
      reset({
        startDate: displayDateUS(episode.startDate) || "",
        endDate: displayDateUS(episode.endDate) || "",
      });
      return;
    }
    const startYmd =
      formatDateForInput(defaultStart) ||
      formatDateForInput(chart?.startDate) ||
      formatDateForInput(new Date());
    reset({
      startDate: ymdToDisplay(startYmd),
      endDate: ymdToDisplay(addDaysYmd(startYmd, EPISODE_OFFSET_DAYS)),
    });
  }, [open, defaultStart, chart, episode, isEdit, reset]);

  useEffect(() => {
    if (!open || isEdit || !startDate) return;
    const ymd = formatDateForInput(startDate);
    if (!ymd) return;
    setValue("endDate", ymdToDisplay(addDaysYmd(ymd, EPISODE_OFFSET_DAYS)));
  }, [startDate, open, isEdit, setValue]);

  if (!open) return null;

  const validate = (values) => {
    const epStart = formatDateForInput(values.startDate);
    const epEnd = formatDateForInput(values.endDate);
    const chartStart = formatDateForInput(chart?.startDate);
    const chartDischarge = formatDateForInput(chart?.dischargeDate);

    if (!epStart || !epEnd) {
      toast.error("Start and end dates are required");
      return false;
    }
    if (parseCmp(epEnd, epStart) < 0) {
      toast.error("Episode end must be on or after start");
      return false;
    }
    if (chartStart && parseCmp(epStart, chartStart) < 0) {
      toast.error("Episode must start on or after the chart start date");
      return false;
    }
    if (chartDischarge && parseCmp(epStart, chartDischarge) > 0) {
      toast.error("Episode must start on or before the chart discharge date");
      return false;
    }
    return true;
  };

  const submit = handleSubmit(async (values) => {
    const latest = getValues();
    const payloadValues = { ...values, ...latest };
    if (!validate(payloadValues)) return;

    const startDateIso = toApiDate(payloadValues.startDate);
    const endDateIso = toApiDate(payloadValues.endDate);
    if (!startDateIso || !endDateIso) {
      toast.error("Invalid dates. Use MM/DD/YYYY");
      return;
    }

    try {
      await onSubmit?.({
        startDate: startDateIso,
        endDate: endDateIso,
      });
    } catch (err) {
      void err;
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">
              {isEdit
                ? `Edit Episode #${episode?.episodeNumber || ""}`
                : "Add a New Episode"}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5 break-words">
              Chart #{chart?.chartNumber}
              {chart?.startDate
                ? ` · ${displayDateUS(chart.startDate)}`
                : ""}
              {chart?.dischargeDate
                ? ` – ${displayDateUS(chart.dischargeDate)}`
                : ""}
              {isEdit
                ? " · Dates can be updated anytime"
                : " · Default window is 60 days (editable)"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 shrink-0"
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={submit} className="p-4 space-y-3" noValidate>
            <InputDatePicker
              label="Start date"
              name="startDate"
              formatSeparator="/"
              allowManualEntry
              allowFuture
              isRequired
              min={chartStartYmd}
            />
            <InputDatePicker
              label="End date"
              name="endDate"
              formatSeparator="/"
              allowManualEntry
              allowFuture
              isRequired
              min={episodeStartYmd || chartStartYmd}
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className={btnCancel}
              >
                Cancel
              </button>
              <button type="submit" disabled={saving} className={btnPrimary}>
                {saving ? (
                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : null}
                {saving
                  ? "Saving…"
                  : isEdit
                    ? "Save episode"
                    : "Create episode"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default EpisodeDateModal;
